
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ShoppingItem } from "@/types";
import { updateItemStatus, deleteShoppingItem } from "@/services/shopping/items/index";
import OfflineLogger from '@/utils/offlineLogger';
import { networkService } from '@/services/network.service';
import { safeToastError, safeToastInfo } from "@/components/ui/toastManager";
import { shoppingItemRepository } from "@/repositories/shoppingItem.repository";
import { useUndoToast } from "@/hooks/useUndoToast";
import { syncService } from '@/services/sync.service';

interface UseItemMutationsProps {
  listId: string;
  onOptimisticUpdate: (items: ShoppingItem[]) => void;
}

export function useItemMutations({ listId, onOptimisticUpdate }: UseItemMutationsProps) {
  const queryClient = useQueryClient();
  const undoToast = useUndoToast();

  const handleCheckItem = useCallback(
    async (id: string, checked: boolean, currentItems: ShoppingItem[]) => {
      try {
        //OfflineLogger.info('useItemMutations.handleCheckItem', `Checking item ${id} as ${checked ? 'checked' : 'unchecked'}`);
        
        // ✅ FIX: Apply optimistic update immediately and ensure it triggers re-render
        const updatedItems = currentItems.map((item) =>
          item.id === id
            ? { ...item, checked, optimisticUpdatedAt: Date.now() }
            : item
        );
        
        onOptimisticUpdate(updatedItems);

        let success = false;
        
        if (networkService.isOnline()) {
          // If online, use Supabase service directly
          success = await updateItemStatus(id, checked);
          
          if (!success) {
            safeToastError("Impossibile aggiornare l'elemento");
            onOptimisticUpdate(currentItems); // Revert optimistic update
            OfflineLogger.error('useItemMutations.handleCheckItem', `Failed to update item ${id}`);
          } else {
            await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
          }
        } else {
          // If offline, use offline repository
          await shoppingItemRepository.updateItemStatus(id, checked);
          safeToastInfo("La modifica è stata salvata nella coda offline");
          success = true;
        }
        
        return success;
      } catch (error) {
        OfflineLogger.error('useItemMutations.handleCheckItem', `Error updating item ${id}: ${error}`);
        
        if (networkService.isOnline()) {
          onOptimisticUpdate(currentItems);
          safeToastError("Impossibile aggiornare l'elemento");
        } else {
          safeToastInfo("La modifica è stata salvata nella coda offline");
        }
        
        return false;
      }
    },
    [onOptimisticUpdate, queryClient, listId]
  );

  const handleDeleteItem = useCallback(
    async (id: string, currentItems: ShoppingItem[]) => {
      const deletedItem = currentItems.find(item => item.id === id) || null;
      try {
        //OfflineLogger.info('useItemMutations.handleDeleteItem', `Deleting item ${id}`);

        // ✅ FIX: Apply optimistic update immediately and ensure it triggers re-render
        const updatedItems = currentItems.filter((item) => item.id !== id);

        onOptimisticUpdate(updatedItems);

        let success = false;

        if (networkService.isOnline()) {
          // If online, use Supabase service directly
          success = await deleteShoppingItem(id);

          if (!success) {
            safeToastError("Impossibile eliminare l'elemento");
            onOptimisticUpdate(currentItems); // Revert optimistic update
            OfflineLogger.error('useItemMutations.handleDeleteItem', `Failed to delete item ${id}`);
          } else {
            await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
            if (deletedItem) {
              undoToast(
                `delete-item-${id}`,
                "Articolo eliminato",
                async () => {
                  try {
                    onOptimisticUpdate([...updatedItems, deletedItem]);
                    queryClient.setQueryData<ShoppingItem[]>(['shopping-items', listId], curr => {
                      return curr ? [...curr, deletedItem] : [deletedItem];
                    });
                    await shoppingItemRepository.create(deletedItem, listId);
                    await syncService.syncPendingOperations();
                    await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
                  } catch (err) {
                    OfflineLogger.error('useItemMutations.undoDelete', `Error restoring item ${id}: ${err}`);
                    safeToastError("Impossibile ripristinare l'elemento");
                  }
                }
              );
            }
          }
        } else {
          // If offline, use offline repository
          await shoppingItemRepository.delete(id);
          safeToastInfo("L'eliminazione è stata salvata nella coda offline");
          success = true;
          if (deletedItem) {
            undoToast(
              `delete-item-${id}`,
              "Articolo eliminato",
              async () => {
                try {
                  onOptimisticUpdate([...updatedItems, deletedItem]);
                  queryClient.setQueryData<ShoppingItem[]>(['shopping-items', listId], curr => {
                    return curr ? [...curr, deletedItem] : [deletedItem];
                  });
                  await shoppingItemRepository.create(deletedItem, listId);
                  await syncService.syncPendingOperations();
                  await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
                } catch (err) {
                  OfflineLogger.error('useItemMutations.undoDelete', `Error restoring item ${id}: ${err}`);
                  safeToastError("Impossibile ripristinare l'elemento");
                }
              }
            );
          }
        }

        return success;
      } catch (error) {
        OfflineLogger.error('useItemMutations.handleDeleteItem', `Error deleting item ${id}: ${error}`);

        if (networkService.isOnline()) {
          onOptimisticUpdate(currentItems);
          safeToastError("Impossibile eliminare l'elemento");
        } else {
          safeToastInfo("L'eliminazione è stata salvata nella coda offline");
        }

        return false;
      }
    },
    [onOptimisticUpdate, queryClient, listId, undoToast]
  );

  return {
    handleCheckItem,
    handleDeleteItem,
  };
}
